import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Clock, Save, Sparkles, Target } from 'lucide-react';
import { Tag } from '../ui/atoms/Tag';
import { Button } from '@/components/ui/atoms/Button';
import { Tooltip } from '../ui/tooltip';
import { DropdownMenu } from '../ui/organisms/DropdownMenu';
import { Tabs } from '@/components/ui/atoms/Tabs';
import { SegmentedButton } from '@/components/ui/atoms/SegmentedButton';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@/components/ui/molecules/InputGroup';

export interface SectionTab {
  id: string;
  number: string;
  title: string;
  icon: ReactNode;
  stats: string;
  /** Optional `data-tour` for product tour (e.g. Technical Skills section). */
  dataTour?: string;
}

interface ExamSectionRailProps {
  tabs: SectionTab[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
  language: string;
  onLanguageChange: (lang: string) => void;
  /** How many days interviews for this job's AI test stay valid. */
  interviewExpiryDays: number;
  /** Called with the new validity window in days. */
  onInterviewExpiryDaysChange: (days: number) => void;
  totalQuestions: number;
  totalMinutes: number;
  onSave: () => void;
  saveDisabled: boolean;
  saving: boolean;
}

const LANGUAGES = [
  { value: 'EN', label: 'English' },
  { value: 'FR', label: 'Français' },
];

// Guardrails for the interview validity window (days).
const MIN_EXPIRY_DAYS = 1;
const MAX_EXPIRY_DAYS = 365;

const clampExpiryDays = (value: number): number =>
  Math.min(MAX_EXPIRY_DAYS, Math.max(MIN_EXPIRY_DAYS, Math.round(value)));

const RailLabel = ({ children }: { children: ReactNode }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-dark-invirtus/40 font-body px-3 mb-1.5">
    {children}
  </p>
);

/**
 * Section navigation for the exam configuration editor. Renders as a vertical
 * rail beside the app sidebar on desktop, and as a compact top bar (section
 * dropdown + language + Save) on narrow screens.
 */
export const ExamSectionRail = ({
  tabs,
  activeTabId,
  onTabChange,
  language,
  onLanguageChange,
  interviewExpiryDays,
  onInterviewExpiryDaysChange,
  totalQuestions,
  totalMinutes,
  onSave,
  saveDisabled,
  saving,
}: ExamSectionRailProps) => {
  const { t } = useTranslation('job');
  const [sectionMenuOpen, setSectionMenuOpen] = useState(false);
  const activeTab = tabs.find((t) => t.id === activeTabId);

  return (
    <>
      {/* Mobile / narrow: compact top bar */}
      <div className="lg:hidden shrink-0 flex flex-wrap items-center gap-2 px-4 py-2.5 bg-light border-b border-light-lila/40">
        <DropdownMenu
          className="flex-1 min-w-[150px]"
          open={sectionMenuOpen}
          onOpenChange={setSectionMenuOpen}
        >
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label={t('exam_config.rail.exam_section')}
              className="w-full h-9 flex items-center justify-between gap-2 px-3 rounded-md border border-light-lila/60 bg-light text-sm font-body text-dark-invirtus transition-colors hover:border-light-lila"
            >
              <span className="flex items-center gap-2 min-w-0">
                {activeTab?.icon && (
                  <span className="shrink-0 text-dark-invirtus/50 [&_svg]:h-4 [&_svg]:w-4">
                    {activeTab.icon}
                  </span>
                )}
                <span className="truncate font-medium">{activeTab?.title}</span>
              </span>
              <span className="flex items-center gap-1.5 shrink-0 text-dark-invirtus/45">
                <span className="text-xs tabular-nums">{activeTab?.stats}</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${sectionMenuOpen ? 'rotate-180' : ''}`}
                />
              </span>
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="start">
            {tabs.map((tab) => {
              const isActive = tab.id === activeTabId;
              return (
                <DropdownMenu.Item
                  key={tab.id}
                  onClick={() => {
                    onTabChange(tab.id);
                    setSectionMenuOpen(false);
                  }}
                  className={isActive ? 'bg-purple-soft/40' : ''}
                >
                  <span
                    className={`shrink-0 [&_svg]:h-4 [&_svg]:w-4 ${
                      isActive ? 'text-dark-invirtus' : 'text-dark-invirtus/40'
                    }`}
                  >
                    {tab.icon}
                  </span>
                  <span
                    className={`flex-1 truncate ${
                      isActive ? 'font-semibold text-dark-invirtus' : ''
                    }`}
                  >
                    {tab.title}
                  </span>
                  <span className="shrink-0 text-xs text-dark-invirtus/45 tabular-nums">
                    {tab.stats}
                  </span>
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu>
        <SegmentedButton
          aria-label={t('exam_config.rail.exam_language')}
          size="sm"
          appearance="filled"
          intent="secondary"
          value={language}
          onValueChange={onLanguageChange}
          options={LANGUAGES.map((lang) => ({
            value: lang.value,
            label: lang.value,
          }))}
        />
        <InputGroup size="sm" className="w-36 shrink-0">
          <InputGroupAddon align="inline-start">
            <Clock className="h-3.5 w-3.5 text-dark-invirtus/50" />
          </InputGroupAddon>
          <InputGroupInput
            type="number"
            min={MIN_EXPIRY_DAYS}
            max={MAX_EXPIRY_DAYS}
            value={interviewExpiryDays}
            onChange={(e) =>
              onInterviewExpiryDaysChange(clampExpiryDays(Number(e.target.value)))
            }
            aria-label={t('exam_config.rail.interview_expiry')}
            startAddonPadding="icon"
            endAddonPadding="icon"
            className="focus:border-light-lila focus:ring-0 focus:ring-offset-0"
          />
          <InputGroupAddon align="inline-end">
            <InputGroupText>
              {t('exam_config.rail.interview_expiry_unit')}
            </InputGroupText>
          </InputGroupAddon>
        </InputGroup>
        <Button
          intent="secondary"
          size="sm"
          className="shrink-0"
          icon={<Save />}
          label={saving ? t('exam_config.actions.saving') : t('exam_config.actions.save')}
          onClick={onSave}
          disabled={saveDisabled}
          loading={saving}
        />
      </div>

      {/* Desktop: vertical rail */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-light border-r border-light-lila/40 overflow-y-auto">
        <div className="px-2.5 pt-6 pb-2.5">
          {/* Sections */}
          <RailLabel>{t('exam_config.rail.sections')}</RailLabel>
          <Tabs
            aria-label={t('exam_config.rail.sections')}
            orientation="vertical"
            color="muted"
            value={activeTabId}
            onValueChange={onTabChange}
            items={tabs.map((tab) => ({
              id: tab.id,
              label: tab.title,
              description: tab.stats,
              icon: tab.icon,
              dataTour: tab.dataTour,
            }))}
          />

          {/* Exam language */}
          <div className="mt-5">
            <RailLabel>{t('exam_config.rail.exam_language')}</RailLabel>
            <SegmentedButton
              aria-label={t('exam_config.rail.exam_language')}
              className="w-full"
              size="sm"
              appearance="raised"
              intent="secondary"
              equalWidth
              value={language}
              onValueChange={onLanguageChange}
              options={LANGUAGES.map((lang) => ({
                value: lang.value,
                label: lang.label,
              }))}
            />
          </div>

          {/* Interview validity window (days) */}
          <div className="mt-5">
            <RailLabel>{t('exam_config.rail.interview_expiry')}</RailLabel>
            <InputGroup size="sm">
              <InputGroupAddon align="inline-start">
                <Clock className="h-3.5 w-3.5 text-dark-invirtus/50" />
              </InputGroupAddon>
              <InputGroupInput
                type="number"
                min={MIN_EXPIRY_DAYS}
                max={MAX_EXPIRY_DAYS}
                value={interviewExpiryDays}
                onChange={(e) =>
                  onInterviewExpiryDaysChange(clampExpiryDays(Number(e.target.value)))
                }
                aria-label={t('exam_config.rail.interview_expiry')}
                startAddonPadding="icon"
                endAddonPadding="icon"
                className="focus:border-light-lila focus:ring-0 focus:ring-offset-0"
              />
              <InputGroupAddon align="inline-end">
                <InputGroupText>
                  {t('exam_config.rail.interview_expiry_unit')}
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </div>

          {/* Save */}
          <div className="mt-6">
            <Button
              intent="secondary"
              size="sm"
              className="w-full"
              icon={<Save />}
              label={saving ? t('exam_config.actions.saving') : t('exam_config.actions.save')}
              onClick={onSave}
              disabled={saveDisabled}
              loading={saving}
            />
          </div>
        </div>

        {/* Legend + totals — pinned to the bottom */}
        <div className="mt-auto">
          <div className="px-2.5 pt-3">
            <RailLabel>{t('exam_config.rail.legend')}</RailLabel>
            <div className="flex flex-col gap-1.5 px-1">
              <Tooltip
                content={t('exam_config.rail.skill_tooltip')}
                variant="top"
                triggerClassName="w-full"
              >
                <Tag
                  intent="primary"
                  variant="subtle"
                  icon={<Target />}
                  fullWidth
                  wrap
                  className="font-body gap-1 cursor-help"
                >
                  {t('exam_config.rail.skill')}
                </Tag>
              </Tooltip>
              <Tooltip
                content={t('exam_config.rail.resume_tailored_tooltip')}
                variant="top"
                triggerClassName="w-full"
              >
                <Tag
                  intent="info"
                  variant="subtle"
                  icon={<Sparkles />}
                  fullWidth
                  wrap
                  className="font-body gap-1 cursor-help"
                >
                  {t('exam_config.rail.resume_tailored')}
                </Tag>
              </Tooltip>
            </div>
          </div>
          <p className="px-4 py-4 text-center text-[11px] font-body text-dark-invirtus/45 tabular-nums">
            {t('exam_config.rail.questions_minutes', { count: totalQuestions, minutes: totalMinutes })}
          </p>
        </div>
      </aside>
    </>
  );
};
